import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { infoToolTip, purpleRedirectIcon } from '../../../assets/hostedassets';
import LoadingSpinner from '../../../common/_custom/LoadingSpinner/LoadingSpinner';
import SideDrawer from '../../../common/_custom/SideDrawer/SideDrawer';
import Tooltip from '../../../common/_custom/Tooltip/Tooltip';
import { isEmpty } from '../../../utils/utils';
import { RISK_MESSAGES_MAP, RISK_HEADER_KEYS, REGULATORY_RISK_SECTIONS } from '../RiskIndicatorConstants';
import {
    RegulatoryRiskDrawerCheckTypeConfig,
    RegulatoryRiskDrawerField,
    RegulatoryRiskFieldType,
    isMatched,
    formatScore,
    toDisplayString,
    toUrlList,
} from '../useRegulatoryRiskDrawerConfig';
import styles from './RegulatoryRiskDrawer.module.scss';

interface RegulatoryRiskDrawerProps {
    checkType: string;
    entityName?: string;
    matchCount: number;
    matches: Record<string, any>[];
    // RECUR-27829. When present, the body splits into Confirmed / Other Probable instead of
    // one flat list. Absent (older BE) => fall back to rendering `matches` flat.
    confirmedMatches?: Record<string, any>[];
    probableMatches?: Record<string, any>[];
    config?: RegulatoryRiskDrawerCheckTypeConfig;
    isLoading?: boolean;
    defaultChipLabel?: string;
    onClose: () => void;
}

// The noun rendered as "View <label> N" — an enum instead of bare 'Source'/'Image' literals.
enum LinkLabel {
    Source = 'Source',
    Image = 'Image',
}

// Long text values (e.g. Description) clamp to 4 lines with a View more / View less toggle.
// The toggle only appears when the text actually overflows the clamp — short cells are untouched.
const TruncatedText = ({ text }: { text: string }) => {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);

    // Reset expansion when the row is reused with new text (positional keys), so overflow is
    // measured against the clamped render, not a stale expanded one.
    useLayoutEffect(() => {
        setExpanded(false);
    }, [text]);

    useLayoutEffect(() => {
        if (expanded) return;
        const el = textRef.current;
        // measured against the clamped render (first paint): scrollHeight > clientHeight ⇒ >4 lines.
        if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [text, expanded]);

    return (
        <span className={styles.FieldValue}>
            <span ref={textRef} className={expanded ? styles.TextExpanded : styles.TextClamped}>
                {text}
            </span>
            {overflowing && (
                <button type="button" className={styles.ViewToggle} onClick={() => setExpanded((e) => !e)}>
                    {expanded ? 'View less' : 'View more'}
                </button>
            )}
        </span>
    );
};

const renderLinkGroup = (value: any, label: LinkLabel) => {
    const urls = toUrlList(value);
    if (urls.length === 0) return <span className={styles.FieldValue}>{toDisplayString(value)}</span>;
    if (urls.length === 1) {
        return (
            <a className={styles.FieldLink} href={urls[0]} target="_blank" rel="noopener noreferrer">
                <img className={styles.LinkIcon} src={purpleRedirectIcon} alt="" aria-hidden="true" />
                View {label}
            </a>
        );
    }
    return (
        <span className={styles.LinkGroup}>
            {urls.map((url, i) => (
                <a className={styles.FieldLink} href={url} target="_blank" rel="noopener noreferrer" key={`${url}-${i}`}>
                    <img className={styles.LinkIcon} src={purpleRedirectIcon} alt="" aria-hidden="true" />
                    View {label} {i + 1}
                </a>
            ))}
        </span>
    );
};

const renderFieldValue = (field: RegulatoryRiskDrawerField, value: any) => {
    switch (field.type) {
        case RegulatoryRiskFieldType.SourceLink:
            return renderLinkGroup(value, LinkLabel.Source);
        case RegulatoryRiskFieldType.ImageLink:
            return renderLinkGroup(value, LinkLabel.Image);
        default:
            // text renders as-is; line breaks preserved via CSS (white-space: pre-line).
            // Non-primitive values (arrays / objects, e.g. Associations) are coerced to a readable string.
            // Clamped to 4 lines with a View more / View less toggle when it overflows.
            return <TruncatedText text={toDisplayString(value)} />;
    }
};

const getFieldValue = (field: RegulatoryRiskDrawerField, hit: Record<string, any>): ReactNode => {
    // 'Other Parameters Matched': the config lists candidate params; the BE hit
    // holds each one's status. Show the labels of the ones that matched, as one row.
    if (field.type === RegulatoryRiskFieldType.MatchedParams) {
        const matchedLabels = (field.params ?? []).filter((p) => isMatched(hit?.[p.rawKey])).map((p) => p.label);
        if (matchedLabels.length === 0) return null;
        return <span className={styles.FieldValue}>{matchedLabels.join(', ')}</span>;
    }
    const value = hit?.[field.rawKey];
    if (isEmpty(value)) return null;
    return renderFieldValue(field, value);
};

/*
    Animated disclosure for a match section.

    Two constraints shape this. The body cannot mount and unmount on toggle, because then
    there is no element for CSS to transition — but it must also stay unmounted until the
    section is opened for the first time: a collapsed Probable section can hold fifty match
    cards, and rendering those up front would slow down the drawer's own open. So it mounts on
    first open and stays mounted, animating on every toggle after that.

    The collapse rides on a grid track (0fr -> 1fr) rather than a max-height, because the
    height is unknown and varies from one card to fifty. A hardcoded max-height would either
    clip a tall section or spend most of the easing travelling through empty space, which is
    what makes that approach feel wrong even when the duration is right. Browsers that cannot
    interpolate a grid track simply open instantly, which is a clean degradation.

    `visibility` transitions alongside so the collapsed body drops out of the tab order and the
    accessibility tree once it has finished collapsing — its "View Source" links must not stay
    focusable while invisible. Browsers hold visibility until the end of a transition to
    hidden and apply it immediately on the way back to visible, which is exactly the behaviour
    wanted here.
*/
/*
    SideDrawer's own entrance (`moveIn`) runs 500ms, holding the panel off-screen until 25% and
    sliding it in over the rest. A section that expands during that window is expanding behind a
    panel the user cannot see yet, so it looks like it was simply open on arrival — measured:
    the body finished expanding at t=409ms, before the panel had landed. The initial expansion
    therefore waits for the drawer to settle; later toggles are immediate.
*/
const DRAWER_ENTRANCE_MS = 500;
/* A short beat after the panel lands, so the two motions read as sequential, not simultaneous. */
const AFTER_ENTRANCE_BEAT_MS = 80;

const CollapsibleSectionBody = ({
    isOpen,
    drawerMountedAt,
    children,
}: {
    isOpen: boolean;
    drawerMountedAt: number;
    children: ReactNode;
}) => {
    const [mounted, setMounted] = useState(isOpen);
    /*
        Starts closed even when the section is already open on mount. A section that opens with
        the drawer — Probable, when there are no confirmed matches — would otherwise render
        straight into its end state and just appear, which is the whole animation missing on
        the path users hit most. The effect below opens it on the next frame instead.
    */
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (isOpen && !mounted) setMounted(true);
    }, [isOpen, mounted]);

    useEffect(() => {
        if (!isOpen) {
            setOpen(false);
            return undefined;
        }
        if (!mounted || open) return undefined;

        /*
            Mounting and opening within one frame would start the transition already at its end
            state, so the section would snap. Applying the open class on a later tick gives the
            browser a painted 0fr to animate away from.
        */
        const settleIn = DRAWER_ENTRANCE_MS + AFTER_ENTRANCE_BEAT_MS - (performance.now() - drawerMountedAt);
        if (settleIn <= 0) {
            // The drawer is already sitting still — this is a user toggle, so open immediately.
            const frame = requestAnimationFrame(() => setOpen(true));
            return () => cancelAnimationFrame(frame);
        }
        const timer = setTimeout(() => setOpen(true), settleIn);
        return () => clearTimeout(timer);
    }, [isOpen, mounted, open, drawerMountedAt]);

    if (!mounted) return null;

    /*
        Three nested elements, each load-bearing:

        - the wrap animates `grid-template-rows: 0fr -> 1fr`, so it collapses to the body's own
          natural height and one card eases as correctly as fifty. A max-height cannot, because
          it would have to guess that height.
        - the clip is separate and deliberately padding-free. Padding survives a zero-height
          content box, so carrying it here instead left a 24px sliver of layout under a closed
          section's header.
        - the body carries all the padding.

        The 320ms in the stylesheet is measured, not taste. What reads as rough or smooth is peak
        velocity, and across this ~900px section peak velocity tracks duration almost exactly:
        10.5px/ms at 240ms, 8.3 at 320ms, 6.0 at 420ms. 240ms whipped through the middle then
        crawled the last tenth; 420ms read as sluggish. A flatter curve was tried on the theory
        that a shorter window could carry a lower peak — it measured 8.3px/ms peak and 2.6
        peak-to-average, against 8.2 and 2.73 for the curve kept here, a difference inside the
        noise of a single-frame maximum. Duration is the only lever that moves this. Zero dropped
        frames at every duration tried; this was never a performance problem.
    */
    return (
        <div className={open ? styles.SectionBodyWrapOpen : styles.SectionBodyWrap}>
            <div className={styles.SectionBodyClip}>
                <div className={styles.MatchSectionBody}>{children}</div>
            </div>
        </div>
    );
};

const RegulatoryRiskDrawer = ({
    checkType,
    entityName,
    matches,
    confirmedMatches,
    probableMatches,
    config,
    isLoading,
    defaultChipLabel = 'Name Matched',
    onClose,
}: RegulatoryRiskDrawerProps) => {
    /*
        This component is mounted only while the drawer is open, so its first render is the
        moment the drawer starts entering. CollapsibleSectionBody measures the entrance against
        this to decide whether it still needs to wait.
    */
    const drawerMountedAtRef = useRef(performance.now());
    const title = config?.title || checkType;
    const chipLabel = config?.chipLabel || defaultChipLabel;
    const fields = config?.fields ?? [];
    const infoText = RISK_MESSAGES_MAP[checkType as RISK_HEADER_KEYS];
    // a hit with no displayable field (e.g. a clean/Green result echoing only the input) renders an empty card.
    // fields is empty while config is still loading (see the config-undefined test below) — don't filter then,
    // there's nothing yet to judge "content" against.
    const hasContent = (hit: Record<string, any>) => fields.length === 0 || fields.some((field) => getFieldValue(field, hit) !== null);
    const renderableMatches = matches.filter(hasContent);

    /*
        RECUR-27829. Both section counts are the RENDERED lengths, not the BE's
        confirmedCount/probableCount — `hasContent` drops hits with nothing displayable, and
        pairing a BE count with a filtered list is the "pill said 3, drawer said 2" defect
        recorded in RECUR-24938.
    */
    const isSplit = Array.isArray(confirmedMatches) || Array.isArray(probableMatches);
    const confirmed = useMemo(() => (confirmedMatches ?? []).filter(hasContent), [confirmedMatches, fields]);
    const probable = useMemo(() => (probableMatches ?? []).filter(hasContent), [probableMatches, fields]);

    /*
        Confirmed opens; Probable stays collapsed behind it — a handful of identity matches
        should not be buried under fifty name-similarity guesses. With no confirmed matches
        there is nothing to bury, so Probable opens instead.
    */
    const [openConfirmed, setOpenConfirmed] = useState(true);
    const [openProbable, setOpenProbable] = useState(false);
    const confirmedCountKey = confirmed.length;
    useLayoutEffect(() => {
        setOpenConfirmed(true);
        setOpenProbable(confirmedCountKey === 0);
    }, [confirmedCountKey, checkType, entityName]);

    const renderHitCard = (hit: Record<string, any>, index: number) => {
        const chipValue = config?.nameMatchKey ? hit?.[config.nameMatchKey] : undefined;
        const showChip = !isEmpty(chipValue);

        return (
            <div className={styles.HitCard} key={index}>
                <div className={styles.HitCardHeader}>
                    <span className={styles.MatchLabel}>Match {index + 1}</span>
                    {showChip && (
                        <span className={styles.NameMatchChip}>
                            {chipLabel} {formatScore(chipValue)}
                        </span>
                    )}
                </div>
                {fields.map((field) => {
                    const fieldValue = getFieldValue(field, hit);
                    if (!fieldValue) return null;
                    return (
                        <div className={styles.FieldRow} key={field.rawKey}>
                            <span className={styles.FieldLabel}>{field.label}</span>
                            {fieldValue}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Match numbering restarts within each section, matching the design ("Match 1" under
    // Confirmed even when Probable holds fifty more).
    const renderSection = (
        section: { title: string; pillBackground: string; pillColor: string },
        hits: Record<string, any>[],
        isOpen: boolean,
        onToggle: () => void,
    ) => (
        <div className={styles.MatchSection} key={section.title}>
            {/*
                Chevron, title and count sit in one left-aligned group. The title must not grow:
                giving it flex-grow is what pushed the count pill to the far right edge of the
                header, away from the heading it belongs to. The count comes from hits.length, the
                rendered rows — never a backend count, which is how the pill and the list came to
                disagree before (RECUR-24938). Uppercasing on the pill is presentational only, so
                the DOM text stays "2 Matches" for the accessible name and the tests.
            */}
            <button type="button" className={styles.MatchSectionHeader} onClick={onToggle} aria-expanded={isOpen}>
                <span className={isOpen ? styles.SectionChevronOpen : styles.SectionChevron} aria-hidden="true" />
                <span className={styles.SectionTitle}>{section.title}</span>
                <span className={styles.SectionCount} style={{ color: section.pillColor, backgroundColor: section.pillBackground }}>
                    {hits.length} {hits.length === 1 ? 'Match' : 'Matches'}
                </span>
            </button>
            <CollapsibleSectionBody isOpen={isOpen} drawerMountedAt={drawerMountedAtRef.current}>
                {hits.map((hit, index) => renderHitCard(hit, index))}
            </CollapsibleSectionBody>
        </div>
    );

    return (
        <SideDrawer
            allowClose
            onClose={onClose}
            width="804px"
            showHeading
            heading={
                <div className={styles.HeaderTitle}>
                    <span className={styles.CheckTypeTitle}>{title}</span>
                    {!!infoText && (
                        // usePortal: HeaderTitle has overflow:hidden (for entity-name ellipsis), which
                        // clips a normally-rendered Tooltip popover. Portal escapes it (same pattern as
                        // MandateSharingDrawer / BasicDetailsSidebar tooltips inside other drawers).
                        <Tooltip content={infoText} direction="top" usePortal>
                            <img className={styles.InfoIcon} src={infoToolTip} alt="Information tooltip" />
                        </Tooltip>
                    )}
                    {!!entityName && (
                        <>
                            <span className={styles.Dot}>|</span>
                            <span className={styles.EntityName}>{entityName}</span>
                        </>
                    )}
                </div>
            }
        >
            <div className={styles.Body}>
                {isLoading && (
                    <div className={styles.LoadingState}>
                        <LoadingSpinner color="#6021B3" height="1.5rem" />
                    </div>
                )}
                {!isLoading && renderableMatches.length === 0 && <div className={styles.EmptyState}>No adverse match found.</div>}
                {!isLoading && renderableMatches.length > 0 && isSplit && (
                    <>
                        {confirmed.length > 0 &&
                            renderSection(REGULATORY_RISK_SECTIONS.CONFIRMED, confirmed, openConfirmed, () => setOpenConfirmed((o) => !o))}
                        {probable.length > 0 &&
                            renderSection(REGULATORY_RISK_SECTIONS.PROBABLE, probable, openProbable, () => setOpenProbable((o) => !o))}
                    </>
                )}
                {!isLoading && !isSplit && renderableMatches.map((hit, index) => renderHitCard(hit, index))}
            </div>
        </SideDrawer>
    );
};

export default RegulatoryRiskDrawer;
